import { useQuery } from "@tanstack/react-query";
import {
  CheckCircle2,
  Clock3,
  Loader2,
  RefreshCw,
} from "lucide-react";

import {
  getInvitationActivity,
  type PlatformAuditEvent,
} from "@/api/platform";

interface InvitationActivityProps {
  churchId: string;
}

function formatDateTime(value: string) {
  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleString();
}

function formatEventType(eventType: string) {
  return eventType
    .replace(/^INVITATION_/, "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase(),
    );
}

function getActivityLabel(
  event: PlatformAuditEvent,
) {
  switch (event.event_type) {
    case "INVITATION_CREATED":
      return "Invitation created";

    case "INVITATION_SENT":
      return "Invitation sent";

    case "INVITATION_RESENT":
      return "Invitation resent";

    case "INVITATION_EMAIL_UPDATED":
    case "INVITATION_EMAIL_CHANGED":
      return "Administrator email changed";

    case "INVITATION_ACCEPTED":
      return "Invitation accepted";

    case "INVITATION_REVOKED":
      return "Invitation revoked";

    case "INVITATION_EXPIRED":
      return "Invitation expired";

    default:
      return formatEventType(
        event.event_type,
      );
  }
}

export function InvitationActivity({
  churchId,
}: InvitationActivityProps) {
  const {
    data,
    isLoading,
    isError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: [
      "invitation-activity",
      churchId,
    ],
    queryFn: () =>
      getInvitationActivity(
        churchId,
      ),
    enabled: Boolean(churchId),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading activity...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-800">
          Invitation activity could not be
          loaded.
        </p>

        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 inline-flex items-center gap-2 text-sm font-medium text-red-800 hover:text-red-950"
        >
          <RefreshCw
            className={`h-4 w-4 ${
              isFetching
                ? "animate-spin"
                : ""
            }`}
          />

          Try again
        </button>
      </div>
    );
  }

  const events = data?.items ?? [];

  if (!events.length) {
    return (
      <p className="text-sm text-slate-500">
        No activity recorded for this
        invitation.
      </p>
    );
  }

  return (
    <div className="space-y-0">
      {events.map((event, index) => (
        <div
          key={event.id}
          className="relative flex gap-3 pb-5 last:pb-0"
        >
          {index < events.length - 1 && (
            <div className="absolute left-2 top-6 h-[calc(100%-1rem)] w-px bg-slate-200" />
          )}

          <div className="relative z-10 mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-white">
            <CheckCircle2 className="h-4 w-4 text-green-600" />
          </div>

          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-900">
              {getActivityLabel(event)}
            </p>

            <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-500">
              <Clock3 className="h-3 w-3" />

              <span>
                {formatDateTime(
                  event.created_at,
                )}
              </span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
